import { TrendChartBarStyles } from "./TrendChartBarStyles";
import { getIsCurrent } from "../../trendChartUtils";
import {
  LOSS_CATEGORIES_STRINGS,
  PROFIT_CATEGORIES_STRINGS_REVERSED,
  CHANGEOVER_DOWNTIME,
  getIsSafari,
  getIsCypressRunning,
} from "constants/constants";
import type {
  RollupWithTagsAndCountsAndRange,
  TimeRangeFinite,
} from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import { useSpring, animated } from "@react-spring/web";
import TrendChartBarTitle from "./TrendChartBarTitle";
import TrendChartCenterMetric from "./TrendChartCenterMetric";
import { Button } from "@mui/material";
import StackOfTrendChartBarSegments from "./StackOfTrendChartBarSegments";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "constants/customers";
import { useRangeFilteredByShift } from "../../TrendChartBarsGetter";

import { BAR_PNL_LOSS_ORDER } from "../trendChartContentUtils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useTrendsPageFilterState,
  useFindDataDumpForTrendChart,
  useSetPnlPopupTimeRange,
  useFormatZoned,
  useVisibleGroups,
  useFindDataDumpForTrendChartCounts,
  useIsSingleProcessPage,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { highlightTags } from "components/Dashboard/PnlSummaryChart/pnlSummaryChartUtils";
import LogRocket from "logrocket";

import {
  TrendChartBarCount,
  useTrendChartOutputDataDumpName,
} from "./TrendChartBarCount";
import { FULL_DATE_YEAR_TIME_ZONE_NO_TEXT } from "utils/timeUtils";
import { useIsDarkMode } from "store/atoms";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import React, { useCallback, useMemo, useTransition } from "react";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { TrendLineSegmentWrapper } from "./TrendLine/TrendLineSegmentWrapper";
import { useRollupMetricForBarRange } from "./trendChartBarUtils";
import { atom, useAtom } from "jotai";
export const trendChartHoveredRangeAtom = atom<TimeRangeFinite | null>(null);
type TrendChartBarProps = {
  idx: number;
  rollupMetric?: RollupWithTagsAndCountsAndRange;
  prevRollupMetric?: RollupWithTagsAndCountsAndRange | null;
  isTrendsPage: boolean;
  isLoaderAnimationEnabled: boolean;
  shouldHideProfitCategories: boolean;
  isClickDisabled: boolean;
  didAnimateOnce: boolean;
  setDidAnimateOnce: any;
  shouldShowHorizontalTargetLine: boolean;
  range: TimeRangeFinite;
};

const TrendChartBar = (
  {
    idx,
    range,
    rollupMetric: overrideRollupMetric,
    prevRollupMetric: overridePrevRollupMetric,
    isTrendsPage,
    isLoaderAnimationEnabled,
    shouldHideProfitCategories,
    isClickDisabled,
    didAnimateOnce,
    setDidAnimateOnce,
    shouldShowHorizontalTargetLine,
  }: TrendChartBarProps // tslint:disable-next-line: no-big-function
) => {
  const rollupMetricOrShiftRange = useRangeFilteredByShift(range);
  const storedRollupMetric = useRollupMetricForBarRange(
    rollupMetricOrShiftRange
  );
  const rollupMetric = useMemo(
    () => overrideRollupMetric || storedRollupMetric,
    [overrideRollupMetric, storedRollupMetric]
  );
  const isStorybook = useIsStorybook();
  const setPnlPopupTimeRange = useSetPnlPopupTimeRange();

  const rollupMetricPossiblyFilteredByShift = useMemo(
    () => ({ ...rollupMetric, range: rollupMetricOrShiftRange }),
    [rollupMetric, rollupMetricOrShiftRange]
  );
  const [isPending, startTransition] = useTransition();
  const handleOpenPnlPopup = useCallback(() => {
    if (!isClickDisabled) {
      LogRocket.track("PNL Popup opened");
      startTransition(() => {
        setPnlPopupTimeRange(rollupMetricPossiblyFilteredByShift.range);
      });
    }
  }, [
    isClickDisabled,
    rollupMetricPossiblyFilteredByShift.range,
    setPnlPopupTimeRange,
    startTransition,
  ]);

  const ckey = useCustomerKey();
  const isFullPageStory = ckey.includes("_page_story"); // having shippable timezone issues only in the full-page stories (Pages.stories.tsx)

  const [{ trendChart }] = useFlightConfig();

  const dataDumpForThisBar = useFindDataDumpForTrendChart({
    geToFind: rollupMetricPossiblyFilteredByShift.range.ge,
  });

  const barDataRange = rollupMetricPossiblyFilteredByShift.range;
  const { isCurrentBar, isFutureBar } = useMemo(() => {
    return {
      isCurrentBar: !barDataRange
        ? false
        : getIsCurrent({
            now: Date.now(),
            rangeGe: barDataRange.ge,
            rangeLt: barDataRange.lt,
          }),

      isFutureBar: Number(barDataRange.ge) > Date.now(),
    };
  }, [barDataRange]);

  // only animate once
  const shouldAnimate =
    isLoaderAnimationEnabled &&
    !isStorybook &&
    !didAnimateOnce &&
    !getIsCypressRunning();

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  const showPlannedAsLossCategory = trendChart.extraLossCategories.includes(
    PnlCategoriesEnum.planned
  );
  const showNotScheduledAsLossCategory =
    trendChart.extraLossCategories.includes(PnlCategoriesEnum.notScheduled);
  const usesAllocationAndChangeovers =
    CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey]?.usesChangeovers;

  const extraLossCategories = useMemo(
    () =>
      !isTrendsPage
        ? [
            ...(isMidgardDDAEnabled ? ["qualityLoss"] : []),
            ...(showPlannedAsLossCategory ? ["planned"] : []),
            ...(showNotScheduledAsLossCategory ? ["notScheduled"] : []),
            ...(usesAllocationAndChangeovers ? [CHANGEOVER_DOWNTIME] : []),
          ]
        : [],
    [
      isTrendsPage,
      isMidgardDDAEnabled,
      showPlannedAsLossCategory,
      showNotScheduledAsLossCategory,
      usesAllocationAndChangeovers,
    ]
  );

  const lossCategoriesSorted = useMemo(
    () =>
      [...extraLossCategories, ...LOSS_CATEGORIES_STRINGS].sort(
        (a, b) => BAR_PNL_LOSS_ORDER[a] - BAR_PNL_LOSS_ORDER[b]
      ),
    [extraLossCategories]
  );
  const bottomBarstackCategories = useMemo(
    () =>
      [...lossCategoriesSorted]
        .reverse()
        .filter(category => !trendChart.hiddenCategories.includes(category)),
    [lossCategoriesSorted, trendChart.hiddenCategories]
  );
  const { showOutputCount, outputCount } = useTrendChartOutputCount(
    barDataRange.ge
  );

  const showTrendChartBarCount = showOutputCount && !isFutureBar;

  const isSafari = getIsSafari();

  const isFirstBarCurrent = idx === 0 && isCurrentBar;
  const [, setTrendChartHoveredRange] = useAtom(trendChartHoveredRangeAtom);
  const doHighlightTags = useCallback(() => {
    setTrendChartHoveredRange(barDataRange);
    highlightTags({
      tag: null,
      doHighlight: true,
      isOverall: false,
      rangeToHighlight: barDataRange,
    });
  }, [barDataRange, setTrendChartHoveredRange]);
  const doUnhighlightTags = useCallback(() => {
    setTrendChartHoveredRange(null);
    highlightTags({
      tag: null,
      doHighlight: false,
      isOverall: false,
      rangeToHighlight: barDataRange,
    });
  }, [barDataRange, setTrendChartHoveredRange]);

  const topBarstackCategories = useMemo(
    () =>
      (shouldHideProfitCategories
        ? lossCategoriesSorted
        : PROFIT_CATEGORIES_STRINGS_REVERSED
      ).filter(category => !trendChart.hiddenCategories.includes(category)),
    [
      shouldHideProfitCategories,
      lossCategoriesSorted,
      trendChart.hiddenCategories,
    ]
  );
  const { dateRangeGe, dateRangeLt } = useDateRangeForBar(
    rollupMetricPossiblyFilteredByShift
  );
  const [isDarkMode] = useIsDarkMode();
  const isEmpty = sumPnlCategories(rollupMetricPossiblyFilteredByShift) === 0;

  return (
    <Button
      disabled={isFutureBar || isPending}
      className={`btnTrendChartBar trendBar-${idx + 1} ${
        isEmpty ? "empty-trend-bar" : ""
      }`}
      onClick={handleOpenPnlPopup}
      onMouseOver={doHighlightTags}
      onMouseOut={doUnhighlightTags}
    >
      <TrendChartBarStyles
        $isSafari={isSafari}
        className={`trend-chart-bar ${
          isFirstBarCurrent ? "firstBarIsCurrentBar" : ""
        } ${
          isCurrentBar ? "currentBar" : isFutureBar ? "futureBar" : "pastBar"
        }`}
        $shouldHideProfitCategories={shouldHideProfitCategories}
        data-range-ge={
          isStorybook || isFullPageStory || isNaN(dateRangeGe)
            ? null
            : dateRangeGe
        }
        data-range-lt={
          isStorybook || isFullPageStory || isNaN(dateRangeLt)
            ? null
            : dateRangeLt
        }
        $isDarkMode={isDarkMode}
      >
        <TrendChartBarTitle
          startTimeMs={Number(range.ge)}
          endTimeMs={Number(range.lt)}
          isCurrentBar={isCurrentBar}
          rollupMetric={rollupMetricPossiblyFilteredByShift}
          dataDumpForThisBar={dataDumpForThisBar}
        />
        {showTrendChartBarCount && (
          <TrendChartBarCount outputCount={outputCount as number} />
        )}
        <AnimatedBarstack
          {...{
            className: `barstack topBarstack ${
              shouldHideProfitCategories ? "loss" : "profit"
            }`,
            isProfit: !shouldHideProfitCategories,
            categoriesStrings: topBarstackCategories,
            lossCategoriesSorted,
            isTrendsPage,
            shouldShowHorizontalTargetLine,
            didAnimateOnce,
            setDidAnimateOnce,
            rollupMetricPossiblyFilteredByShift,
            shouldAnimate,
          }}
        />
        <TrendChartCenterMetric
          rollupMetric={rollupMetricPossiblyFilteredByShift}
          barRange={barDataRange}
        />
        {shouldHideProfitCategories ? null : (
          <AnimatedBarstack
            {...{
              className: `barstack bottomBarstack loss`,
              isProfit: false,
              categoriesStrings: bottomBarstackCategories,
              lossCategoriesSorted,
              isTrendsPage,
              shouldShowHorizontalTargetLine,
              didAnimateOnce,
              setDidAnimateOnce,
              rollupMetricPossiblyFilteredByShift,
              shouldAnimate,
            }}
          />
        )}
        <TrendLineSegmentWrapper
          {...{
            barDataRange,
            range,
            rollupMetric: rollupMetricPossiblyFilteredByShift,
            overridePrevRollupMetric,
            showOutputCount,
            shouldHideProfitCategories,
          }}
        />
      </TrendChartBarStyles>
    </Button>
  );
};

const AnimatedBarstack = React.memo<{
  isProfit: boolean;
  categoriesStrings: string[];
  isTrendsPage: boolean;
  shouldShowHorizontalTargetLine: boolean;
  rollupMetricPossiblyFilteredByShift: RollupWithTagsAndCountsAndRange;
  shouldAnimate: boolean;
  setDidAnimateOnce: any;
  didAnimateOnce: boolean;
  className: string;
}>(
  ({
    isProfit,
    categoriesStrings,
    isTrendsPage,
    shouldShowHorizontalTargetLine,
    rollupMetricPossiblyFilteredByShift,
    shouldAnimate,
    setDidAnimateOnce,
    didAnimateOnce,
    className,
  }) => {
    const springScaleY = useSpring({
      from: {
        transform: `scaleY(${shouldAnimate ? 0 : 1})`,
      },
      to: {
        transform: `scaleY(1)`,
      },
      onRest: () => {
        // stop animating after we finish animating in
        if (!didAnimateOnce) {
          setDidAnimateOnce(true);
        }
      },
    });

    const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

    const { isTrendsPageFilteredByTag, isTrendsPageFilteredByPnlCategory } =
      useTrendsPageFilterState();

    const isTrendsPageFilteredByTagOrCategory = Boolean(
      isTrendsPage &&
        (isTrendsPageFilteredByTag || isTrendsPageFilteredByPnlCategory)
    );

    const shouldScaleRelativeToBiggestBar =
      isTrendsPageFilteredByTagOrCategory ||
      isMidgardDDAEnabled ||
      shouldShowHorizontalTargetLine;

    return (
      <animated.div style={springScaleY} className={`barstack ${className}`}>
        <StackOfTrendChartBarSegments
          categoriesStrings={categoriesStrings}
          isProfit={isProfit}
          shouldScaleRelativeToBiggestBar={shouldScaleRelativeToBiggestBar}
          rollupMetric={rollupMetricPossiblyFilteredByShift}
        />
      </animated.div>
    );
  }
);

export default TrendChartBar;

function useDateRangeForBar(
  rollupMetricPossiblyFilteredByShift: RollupWithTagsAndCountsAndRange
) {
  const formatZoned = useFormatZoned();

  const geFormatZoned = formatZoned(
    Number(rollupMetricPossiblyFilteredByShift.range.ge),
    FULL_DATE_YEAR_TIME_ZONE_NO_TEXT
  );
  const dateRangeGe = new Date(geFormatZoned).getTime();

  const ltFormatZoned = formatZoned(
    Number(rollupMetricPossiblyFilteredByShift.range.lt),
    FULL_DATE_YEAR_TIME_ZONE_NO_TEXT
  );
  const dateRangeLt = new Date(ltFormatZoned).getTime();
  return { dateRangeGe, dateRangeLt };
}

function useTrendChartOutputCount(geToFind: number) {
  const dataDumpForCurrentRange = useFindDataDumpForTrendChart({
    geToFind,
  });
  const [flightConfig] = useFlightConfig();

  // override machemetry ids if specified in flightConfig
  const processGroup = useVisibleGroups()[0];
  const overrideMachemetryIds =
    !processGroup || !flightConfig.trendChart.overrideCountMachemetryIdsPerGroup
      ? null
      : (flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.shortName
        ] ??
        flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.longName
        ] ??
        null);

  const dataDumpNameForOutput = useTrendChartOutputDataDumpName();

  const dataDumpCountsForCurrentRange = useFindDataDumpForTrendChartCounts({
    geToFind,
  });
  const isSingleProcessPage = useIsSingleProcessPage();
  const dataDump =
    overrideMachemetryIds && !isSingleProcessPage
      ? dataDumpCountsForCurrentRange
      : dataDumpForCurrentRange;
  const outputCount = dataDump?.[dataDumpNameForOutput] ?? null;
  return { showOutputCount: flightConfig.trendChart.showCounts, outputCount };
}

function sumPnlCategories(rollupMetric: RollupWithTagsAndCountsAndRange) {
  return (
    rollupMetric.producing +
    rollupMetric.planned +
    rollupMetric.unplanned +
    rollupMetric.waiting +
    rollupMetric.unknown +
    rollupMetric.notScheduled +
    rollupMetric.uncollected
  );
}
